import { Link } from 'react-router-dom';
import { Bell, UserPlus, UserRoundSearch, AlarmClock, ShieldAlert, MailWarning, CirclePause } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NotificationActions } from './NotificationActions';
import type { AppNotification } from './types';

const ICONS: Record<string, typeof Bell> = {
  'lead.assigned': UserPlus,
  // #733
  'lead.needs_owner': UserRoundSearch,
  'lead.owner_overdue': AlarmClock,
  // #343 — scheduled report owner notifications (UI-SPEC Surface 5)
  'report.schedule_halted': ShieldAlert,
  'report.schedule_partial': MailWarning,
  'report.schedule_paused': CirclePause,
};

interface NotificationRowProps {
  notification: AppNotification;
  onNavigate?: () => void;
}

export function NotificationRow({ notification, onNavigate }: NotificationRowProps) {
  const Icon = ICONS[notification.type] ?? Bell;
  const hasActions = (notification.data?.actions?.length ?? 0) > 0;

  const body = (
    <div className="flex items-start gap-3 px-4 py-3">
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-full',
          notification.is_read ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary',
        )}
      >
        <Icon className="size-4.5" aria-hidden />
      </span>
      <div className="flex flex-col gap-0.5">
        <span className={cn('text-sm', notification.is_read ? 'font-normal' : 'font-medium')}>
          {notification.title}
        </span>
        {notification.created_at && (
          <span className="text-xs text-muted-foreground">
            {new Date(notification.created_at).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );

  // #733 — the action bar sits OUTSIDE the Link. Nesting interactive controls
  // inside an anchor is invalid HTML and breaks keyboard activation.
  return (
    <div>
      {notification.action_url ? (
        <Link to={notification.action_url} onClick={onNavigate} className="block hover:bg-muted/50">
          {body}
        </Link>
      ) : (
        body
      )}
      {hasActions && <NotificationActions notification={notification} />}
    </div>
  );
}
